"use client";

import { useState } from "react";
import Button, { ButtonVariant } from "@/app/(components)/generics/Button/Button";
import AddressInput from "@/app/(components)/generics/inputs/default/AddressInput/AddressInput";
import ChoiceInput from "@/app/(components)/generics/inputs/default/ChoiceInput/ChoiceInput";
import { Input, TextInputType } from "@/app/(components)/generics/inputs/default/Input/Input";
import PhoneInput from "@/app/(components)/generics/inputs/default/PhoneInput/PhoneInput";
import ErrorMessage from "@/app/(components)/generics/messages/ErrorMessage/ErrorMessage";
import PracticeNameInput from "@/app/(components)/specifics/users/Users/components/PracticeNameInput/PracticeNameInput";
import { createAdminUser, sendAdminInviteEmail } from "@/app/actions/admin/action";
import { ModalStore } from "@/app/stores/ModalStore";
import { TKey } from "@/app/translations/TKey";
import { T, TErrors } from "@/app/translations/Translations";
import { PracticeResult } from "@/app/types/payload/response/PracticeResult";
import { getProfessions, PROFESSIONAL_ID_TYPES } from "@/app/vars/PractitionerVars";
import styles from "./AddUser.module.css";

enum AddUserStep {
  FORM = "FORM",
  CONFIRM_INVITE = "CONFIRM_INVITE",
  DONE = "DONE",
}

type Props = { lang: string; onSuccess?: () => void };

export default function AddUser({ lang, onSuccess }: Props) {
  const { closeModal } = ModalStore();

  const professions = getProfessions(lang);

  const [data, setData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    profession: "",
    professionOther: "",
    professionalIdType: "",
    professionalIdNumber: "",
    practiceName: "",
    practiceAddress: "",
    practiceCity: "",
    practicePostalCode: "",
    practiceCountry: "",
  });

  const [isPractitioner, setIsPractitioner] = useState(false);
  const [hasPractice, setHasPractice] = useState(false);
  const [selectedPractice, setSelectedPractice] = useState<PracticeResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<AddUserStep>(AddUserStep.FORM);
  const [createdUser, setCreatedUser] = useState<{
    firstName: string;
    lastName: string;
    email: string;
  } | null>(null);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setFieldErrors({});
    setLoading(true);

    const profession = data.profession || data.professionOther;

    const result = await createAdminUser(
      {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone,
        isPractitioner,
        profession,
        professionalIdType: data.professionalIdType,
        professionalIdNumber: data.professionalIdNumber,
        hasPractice: isPractitioner && hasPractice,
        practiceId: selectedPractice?.id,
        practiceName: data.practiceName,
        practiceAddress: data.practiceAddress,
        practiceCity: data.practiceCity,
        practicePostalCode: data.practicePostalCode,
        practiceCountry: data.practiceCountry,
      },
      lang,
    );

    if (result.success) {
      setCreatedUser({ firstName: data.firstName, lastName: data.lastName, email: data.email });
      setStep(AddUserStep.CONFIRM_INVITE);
      onSuccess?.();
    } else if (result.fieldErrors) {
      setFieldErrors(TErrors(result.fieldErrors, lang));
    } else {
      setError(T(TKey.ERROR, lang) as string);
    }

    setLoading(false);
  };

  if (step === AddUserStep.CONFIRM_INVITE && createdUser) {
    const handleSendInvite = async () => {
      setLoading(true);
      await sendAdminInviteEmail(createdUser.email, lang);
      setLoading(false);
      setStep(AddUserStep.DONE);
    };

    return (
      <div className={styles.sentState}>
        <p className={styles.sentText}>{T(TKey.USER_ADDED, lang)}</p>
        <p className={styles.createdName}>
          {createdUser.firstName} {createdUser.lastName}
        </p>
        <p className={styles.inviteQuestion}>{T(TKey.INVITE_OR_SKIP, lang)}</p>
        <div className={styles.actions}>
          <Button variant={ButtonVariant.SECONDARY} onClick={closeModal}>
            {T(TKey.SKIP, lang)}
          </Button>
          <Button variant={ButtonVariant.PRIMARY} loading={loading} onClick={handleSendInvite}>
            {T(TKey.SEND_INVITATION, lang)}
          </Button>
        </div>
      </div>
    );
  }

  if (step === AddUserStep.DONE) {
    return (
      <div className={styles.sentState}>
        <p className={styles.sentText}>{T(TKey.INVITE_USER_SENT, lang)}</p>
        <p className={styles.sentEmail}>{createdUser?.email}</p>
      </div>
    );
  }

  return (
    <form className={styles.container} onSubmit={handleSubmit}>
      {/* Basic info */}
      <div className={styles.row}>
        <Input
          data={data}
          dataKey="firstName"
          label={T(TKey.FIRST_NAME, lang) as string}
          lang={lang}
          required
          showRequiredLabel
          placeholder={T(TKey.FIRST_NAME, lang) as string}
          onChange={(d) => setData({ ...d })}
          errorMessage={fieldErrors.firstName}
        />
        <Input
          data={data}
          dataKey="lastName"
          label={T(TKey.LAST_NAME, lang) as string}
          lang={lang}
          required
          showRequiredLabel
          placeholder={T(TKey.LAST_NAME, lang) as string}
          onChange={(d) => setData({ ...d })}
          errorMessage={fieldErrors.lastName}
        />
      </div>

      <Input
        data={data}
        dataKey="email"
        type={TextInputType.EMAIL}
        label={T(TKey.EMAIL, lang) as string}
        lang={lang}
        required
        showRequiredLabel
        placeholder={T(TKey.EMAIL, lang) as string}
        onChange={(d) => setData({ ...d })}
        errorMessage={fieldErrors.email}
      />

      <PhoneInput
        data={data}
        dataKey="phone"
        label={T(TKey.PHONE, lang) as string}
        lang={lang}
        showRequiredLabel
        placeholder={T(TKey.PHONE, lang) as string}
        onChange={(d) => setData({ ...d })}
        errorMessage={fieldErrors.phone}
      />

      {/* Practitioner toggle */}
      <label className={styles.toggle}>
        <input
          type="checkbox"
          className={styles.toggleCheckbox}
          checked={isPractitioner}
          onChange={(e) => {
            setIsPractitioner(e.target.checked);
            if (!e.target.checked) setHasPractice(false);
          }}
        />
        <span className={styles.toggleSwitch} />
        <span className={styles.toggleLabel}>{T(TKey.ADD_AS_PRACTITIONER, lang)}</span>
      </label>

      {/* Practitioner fields */}
      {isPractitioner && (
        <div className={styles.section}>
          <ChoiceInput
            label={T(TKey.SETTINGS_PROFESSION, lang) as string}
            lang={lang}
            isRequired
            showRequiredLabel
            data={data}
            dataKey="profession"
            options={professions}
            otherOption={{
              dataKey: "professionOther",
              text: T(TKey.SETTINGS_PROFESSION_OTHER_LABEL, lang) as string,
              input: {
                data,
                dataKey: "professionOther",
                placeholder: T(TKey.SETTINGS_PROFESSION_OTHER_PLACEHOLDER, lang) as string,
                onChange: (d) => setData({ ...d }),
              },
            }}
            required={{ visible: !!fieldErrors.profession, text: fieldErrors.profession }}
          />

          <ChoiceInput
            label={T(TKey.SETTINGS_PROFESSIONAL_ID_TYPE, lang) as string}
            lang={lang}
            isRequired
            showRequiredLabel
            data={data}
            dataKey="professionalIdType"
            options={PROFESSIONAL_ID_TYPES}
            required={{
              visible: !!fieldErrors.professionalIdType,
              text: fieldErrors.professionalIdType,
            }}
          />

          <Input
            data={data}
            dataKey="professionalIdNumber"
            label={T(TKey.SETTINGS_PROFESSIONAL_ID_NUMBER, lang) as string}
            lang={lang}
            required
            showRequiredLabel
            placeholder={T(TKey.SETTINGS_PROFESSIONAL_ID_NUMBER, lang) as string}
            onChange={(d) => setData({ ...d })}
            errorMessage={fieldErrors.professionalIdNumber}
          />

          {/* Practice toggle */}
          <label className={styles.toggle}>
            <input
              type="checkbox"
              className={styles.toggleCheckbox}
              checked={hasPractice}
              onChange={(e) => setHasPractice(e.target.checked)}
            />
            <span className={styles.toggleSwitch} />
            <span className={styles.toggleLabel}>{T(TKey.SETTINGS_PRACTICE, lang)}</span>
          </label>

          {/* Practice fields */}
          {hasPractice && (
            <div className={styles.section}>
              <PracticeNameInput
                value={data.practiceName}
                selectedPractice={selectedPractice}
                label={T(TKey.PRACTICE_NAME, lang) as string}
                lang={lang}
                required
                showRequiredLabel
                errorMessage={fieldErrors.practiceName}
                onChange={(name, practice) => {
                  setSelectedPractice(practice);
                  setData({ ...data, practiceName: name });
                }}
              />
              {!selectedPractice && (
                <AddressInput
                  address={data.practiceAddress}
                  city={data.practiceCity}
                  postalCode={data.practicePostalCode}
                  country={data.practiceCountry}
                  lang={lang}
                  showRequiredLabel
                  addressError={fieldErrors.practiceAddress}
                  cityError={fieldErrors.practiceCity}
                  countryError={fieldErrors.practiceCountry}
                  onChange={(address, city, postalCode, country) =>
                    setData({
                      ...data,
                      practiceAddress: address,
                      practiceCity: city,
                      practicePostalCode: postalCode,
                      practiceCountry: country,
                    })
                  }
                />
              )}
            </div>
          )}
        </div>
      )}

      {error && <ErrorMessage message={error} />}

      <div className={styles.actions}>
        <Button variant={ButtonVariant.SECONDARY} onClick={closeModal}>
          {T(TKey.CANCEL, lang)}
        </Button>
        <Button variant={ButtonVariant.PRIMARY} type="submit" loading={loading}>
          {T(TKey.ADD_USER, lang)}
        </Button>
      </div>
    </form>
  );
}
